// EditTeamDialog — admin-side manual team editor (PRODOPSAI-540).
//
// `team_key` is rendered read-only (greyed). Editable: team_name, region,
// productId (PRODOPSAI-542 — sourced from productApi.getAll(), one-fetch-per-
// open). Submits PATCH /api/admin/teams/:id. The button is hidden on
// Fabric-synced rows by `AdminTeamsPage` — but if a row's fabric_synced flips
// between page load and PATCH, the backend responds 400 TEAM_IS_FABRIC_SYNCED
// which this dialog surfaces as a defensive ErrorBanner.

import { useEffect, useState } from 'react';
import type { ITeam } from '@skystone/shared';
import {
  Button,
  Dialog,
  DialogFooter,
  ErrorBanner,
} from '@/components/ui';
import { inputClass } from '@/lib/form-styles';
import { getErrorMessage } from '@/lib/utils';
import { teamApi } from '@/services/team-api';
import {
  REGION_NAME_MAX,
  TEAM_NAME_MAX,
  buildUpdateBody,
  canSaveUpdate,
} from './team-form-helpers';
import { TeamProductPickerField } from './TeamProductPickerField';

interface EditTeamDialogProps {
  open: boolean;
  team: ITeam | null;
  onClose: () => void;
  onSaved: (updated: ITeam) => void;
}

export function EditTeamDialog({
  open,
  team,
  onClose,
  onSaved,
}: Readonly<EditTeamDialogProps>) {
  const [teamName, setTeamName] = useState<string>('');
  const [regionName, setRegionName] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the form whenever the dialog opens with a team.
  useEffect(() => {
    if (open === false || team === null) {
      setTeamName('');
      setRegionName('');
      setProductId('');
      setSaveError(null);
      setSaving(false);
      return;
    }
    setTeamName(team.name);
    setRegionName(team.regionName ?? '');
    // PRODOPSAI-542: pre-select the team's current product (empty string when
    // null/undefined — matches the "(no product)" sentinel in the picker).
    setProductId(team.productId ?? '');
    setSaveError(null);
    setSaving(false);
  }, [open, team]);

  const saveable = canSaveUpdate({ teamName }) && saving === false && team !== null;

  const handleSave = async () => {
    if (saveable === false || team === null) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body = buildUpdateBody({ teamName, regionName, productId });
      const updated = await teamApi.update(team.id, body);
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      setSaveError(getErrorMessage(err, 'Failed to update team'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Edit team" maxWidth="max-w-lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        data-testid="edit-team-form"
      >
        <div>
          <label htmlFor="edit-team-key" className="mb-1 block text-sm font-medium">
            Team key
          </label>
          <input
            id="edit-team-key"
            type="text"
            // Rabizard #7: bind to `teamKey` (the canonical immutable id from
            // raw_dim_team.team_key) rather than `fabricTeamId`. Manual teams
            // populate team_key from the admin form but conceptually have no
            // Fabric mapping; binding to fabricTeamId rendered a blank key
            // field for those rows.
            value={team?.teamKey ?? ''}
            readOnly
            className="block w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
            data-testid="edit-team-key"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            team_key is immutable. Create a new team if you need a different key.
          </p>
        </div>

        <div>
          <label htmlFor="edit-team-name" className="mb-1 block text-sm font-medium">
            Team name <span className="text-error-500">*</span>
          </label>
          <input
            id="edit-team-name"
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            maxLength={TEAM_NAME_MAX + 10}
            className={inputClass}
            data-testid="edit-team-name"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="edit-team-region" className="mb-1 block text-sm font-medium">
            Region
          </label>
          <input
            id="edit-team-region"
            type="text"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
            maxLength={REGION_NAME_MAX + 10}
            className={inputClass}
            data-testid="edit-team-region"
          />
        </div>

        {/* PRODOPSAI-542: product picker extracted to a shared field (CPD).
            Dialog returns null when !open, so this remounts on each open,
            triggering one productApi.getAll() per session. */}
        <TeamProductPickerField
          value={productId}
          onChange={setProductId}
          idPrefix="edit-team"
        />

        {saveError !== null && <ErrorBanner message={saveError} />}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={saving}
            disabled={saveable === false}
            data-testid="edit-team-save"
          >
            Save changes
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
